<template>
  <div v-if="hasOpenModal" class="player-modals">
    <transition name="zoom">
      <div v-if="showEmailModal" class="page-frame email">
        <i @click="closeEmailModal()" class="fas fa-times close" />
        <h3 class="title is-3">Want your final results?</h3>
        <p>Get a recap of your answers, word clouds, scores, and other slides when the event is over. <br>(We'll send one email only.)</p>
        <!-- <p>When the event is over we'll send you a recap so you can review and remember everything.</p> -->
        <b-field>
          <b-input
            ref="emailInput"
            :expanded="true"
            v-model="playerEmail"
            @input="checkEmailSpelling()"
            :placeholder="`Your email`"
            icon-pack="fas"
            icon="envelope"
            size="is-medium"
            type="email"
            class="is-fullwidth m-t-30"
          />
          <span v-if="suggestion" @click="setSuggestedEmail()" class="suggestion">do you mean <u>{{ suggestion }}</u>?</span>
        </b-field>

        <button @click="saveAndClose($event)" class="button is-medium is-primary is-fullwidth">Join event with recap</button>
        <p v-if="isFreeEvent" class="is-small" style="display: inline-block;">
          <b-checkbox v-model="addToNewsletter" />
          I also want your newsletter (with new games and slide decks like this)
        </p>

        <button @click="closeEmailModal()" class="button is-outlined is-primary is-fullwidth m-t-30">Skip and continue</button>
      </div>
      <div v-else-if="showRatingModal" class="toast-frame rating">
        <i @click="closeRatingModal()" class="fas fa-times close" />

        <template v-if="!player.ratingSent">
          <h3 class="title is-4">How did you like this event?</h3>
          <div :class="status" class="buttons has-addons is-centered">
            <div
              v-for="(emoji, key) in emojiScale"
              @click="startSaveRating(key + 1, $event)"
              :key="'rating-' + key"
              class="button is-medium"
            >
              {{ emoji }}
            </div>
          </div>
          <br>
        </template>
        <template v-else>
          <h3 class="title is-4">{{ feedbackQuestion }}</h3>
          <b-input v-model="comment" type="textarea" />
          <br>
          <div @click="startSaveComment($event)" class="button is-primary is-medium is-fullwidth">Send &amp; Close</div>
          <a v-if="!recapDisabled" :href="'/recap/' + $parent.event.id" class="button is-text is-fullwidth m-t-10">Skip and go to the event recap</a>
        </template>
      </div>
      <div v-if="showEndModal" class="page-frame email">
        <h3 class="title is-3">This event has ended!</h3>

        <template v-if="!recapDisabled">
          <p>Click the button below for a recap of your answers, word clouds, scores, and other slides.</p>
          <a :href="'/recap/' + $parent.event.id" class="button is-primary is-medium is-fullwidth m-t-30">Check out the recap &rarr;</a>
        </template>
        <template v-else>
          <p>Want to create your own interactive event like this? Click the button below to create an acount and get started!</p>
          <a :href="'/welcome-participant/'" class="button is-primary is-medium is-fullwidth m-t-30">Create your own event &rarr;</a>
        </template>
      </div>
    </transition>
  </div>
</template>
<script>
import emailSpellChecker from '@zootools/email-spell-checker'
import { savePlayerEmail } from '~/queries/playerQueries.gql'
import { insertFeedback } from '~/queries/feedback.gql'
import { validateEmail } from '~/common/functions.js'

export default {
  props: ['slides', 'currentSlide', 'eventMeta'],
  data () {
    return {
      playerEmail: '',
      suggestion: null,
      closedModals: [],
      addToNewsletter: false,
      question: 'Player: How did you like this event?',
      questionId: 5, // database id of this question for Players,
      feedbackQuestion: 'Leave a private comment for the host 👏',
      feedbackQuestionId: 6, // database id of this question for Players
      emojiScale: ['😩', '😟', '😐', '😊', '😄'],
      status: '',
      rating: null,
      comment: ''
    }
  },
  computed: {
    activeEvent () {
      return this.$store.state.activeEvent
    },
    player () {
      return this.activeEvent.player
    },
    eventSettings () {
      return this.$parent.settings || {}
    },
    isFreeEvent () {
      return this.eventSettings.hostPlan === 'free'
    },
    isActiveEvent () {
      return this.$parent.event.isActive
    },
    isPreviewEvent () {
      return this.$parent?.event?.settings?.isPreviewEvent || false
    },
    recapDisabled () {
      return this.eventSettings.disableRecap === true
    },
    showEmailModal () {
      if (this.recapDisabled) { return false }
      if (this.currentSlide.order > 1) { return false }
      if (['optional', 'doNotCollect'].includes(this.eventSettings.collectPlayerEmails)) { return false }
      if (!this.player?.id || this.player.closedEmailModal === true) { return false }
      if (this.player.email && this.player.email.length > 0) { return false }

      return true
    },
    showRatingModal () {
      if (this.slides.length < 1) { return false }
      if (this.player.closedRatingModal === true) { return false }
      if (this.eventSettings.disableRatings === true) { return false }

      const staticSlides = ['titleSlide', 'contentSlide', 'scoreSummary', 'answerSummary']
      const slidesFromEnd = this.slides.length - this.currentSlide.order
      const isStatic = staticSlides.includes(this.currentSlide.template)

      // making this only the last slide for now
      return (isStatic && slidesFromEnd < 1) || slidesFromEnd === 0 || !this.isActiveEvent
    },
    showEndModal () {
      return !this.isActiveEvent && !this.showRatingModal
    },
    playerRatingSent () {
      return this.player.ratingSent
    },
    hasOpenModal () {
      if (!this.$parent.event.id) { return false }
      if (this.isPreviewEvent) { return false }
      return this.showEmailModal || this.showRatingModal || this.showEndModal
    },
    posthogProps () {
      return {
        event_id: this.activeEvent.id,
        shortcode: this.activeEvent.shortcode,
        player_id: this.player?.id,
        host_id: this.$parent?.eventMeta?.primaryUser
      }
    }
  },
  methods: {
    async saveAndClose ($event) {
      const input = this.$refs.emailInput
      this.log('player email ref is valid? ', input.isValid)
      if (!input || !input.value || input.value.length < 5 || !input.isValid) {
        this.$buefy.toast.open('Enter a valid email address to get an event recap')
        return
      }

      $event.target.classList.add('is-loading')
      await this.validateEmailAndSave()

      if (this.addToNewsletter) {
        await this.addPlayerToNewsletter()
      }

      const posthogProps = { ...this.posthogProps, player_email: this.playerEmail }
      this.posthogCapture('player:email_add', posthogProps)

      $event.target.classList.remove('is-loading')
      this.closeEmailModal()
    },
    async addPlayerToNewsletter () {
      const apiKey = '7beb3b7d-c246-4fb8-bfa1-3c9f140ba327' // Cecilia's account & api key
      const listId = 'de11ffa2-ba75-11ec-9258-0241b9615763' // list in Cecilia's account

      const data = {
        api_key: apiKey,
        email_address: this.playerEmail,
        tags: [
          'player'
        ]
      }

      try {
        const resp = await this.$axios.post(process.env.SITE_URL + `/email-proxy/api/1.6/lists/${listId}/contacts`, data)
        this.log(resp)
      } catch (e) {
        this.throwError(e.message)
      }

      return true
    },
    checkEmailSpelling () {
      const suggestion = emailSpellChecker.run({ email: this.playerEmail })
      if (suggestion) {
        this.suggestion = suggestion.full
      } else {
        this.suggestion = null
      }
    },
    setSuggestedEmail () {
      this.playerEmail = this.suggestion
      this.suggestion = null
    },
    async validateEmailAndSave () {
      try {
        const validationResponse = await validateEmail(this.playerEmail)
        await this.savePlayerEmail(validationResponse)
      } catch (e) {
        this.log(e.message)
      }
    },
    async savePlayerEmail (validationResponse) {
      await this.$apollo.mutate({
        mutation: savePlayerEmail,
        variables: {
          email: this.playerEmail,
          participantId: this.player.id,
          primaryUserId: this.eventMeta.primaryUser,
          eventId: this.eventMeta.id,
          validation: validationResponse
        }
      })
    },
    closeEmailModal () {
      const player = { ...this.player }
      player.closedEmailModal = true
      this.savePlayer(player)
    },
    closeRatingModal () {
      const player = { ...this.player }
      player.closedRatingModal = true
      this.savePlayer(player)
    },
    savePlayer (player) {
      this.$store.commit('setActivePlayer', player)
    },
    async startSaveRating (rating, $event) {
      this.rating = rating
      $event.target.classList.add('is-loading')
      await this.saveFeedback(rating, this.question, this.questionId)
      $event.target.classList.remove('is-loading')

      // save sent rating to vuex
      const player = { ...this.player }
      player.ratingSent = true
      this.savePlayer(player)

      this.posthogCapture('player:rating_send', { ...this.posthogProps, player_rating: rating })
    },
    async startSaveComment ($event) {
      $event.target.classList.add('is-loading')
      await this.saveFeedback(this.comment, this.feedbackQuestion, this.feedbackQuestionId)
      $event.target.classList.remove('is-loading')

      // close rating modal
      this.closeRatingModal()
      this.$buefy.toast.open({ type: 'is-primary', message: 'Feedback received, thank you!' })
    },
    async saveFeedback (feedback, question, questionId) {
      const hostId = this.$parent.event.primaryUser || null
      await this.$apollo.mutate({
        mutation: insertFeedback,
        variables: {
          response: feedback.toString(),
          questionId,
          userId: hostId,
          participantId: this.player.id,
          questionText: question,
          eventId: this.$parent.event.id,
          url: process.env.SITE_URL + this.$route.path
        }
      })
    }
  }
}
</script>

<style lang="scss" scoped>

.suggestion {
  display: block;
  background-color: rgb(243, 230, 153);
  color: #444;
  padding: 8px 16px;
  border-radius: 8px;
  margin-top: 10px;
  font-size: 14px;
  cursor: pointer;
  margin-bottom: 24px;
}

p.is-small {
  font-size: .8em;
  margin-top: 10px;
}

.player-modals {
  position: fixed;
  top: 3em; bottom: 1.4em;
  right: 1.5em;
  left: 1.5em;
  z-index: 100;
  display: flex;
  justify-content: center;
  align-items: center;

  .close {
    position: absolute;
    font-size: 1.2em;
    padding: .75em;
    // border: 1px solid #fafafa;
    top: 0em;
    right: 0em;
    color: #aaa;
  }
}

.page-frame {
  background-color: white;
  border-radius: 10px;
  height: 100%;
  width: 100%;
  padding: 3em 2em 2em;
  text-align: center;
  gap: 0em;
}

.b-checkbox.checkbox {
  line-height: 1em;
  margin-right: -7px;
  top:7px;
  font-size: 1.08em;
}

.toast-frame {
  background-color: white;
  border-radius: 10px;
  // height: 60%;
  width: 100%;
  padding: 3em 1.5em 2em;
  text-align: center;
  gap: 0em;
  position: relative;
  box-shadow: 0px 5px 75px 140px rgba(0,0,0,.15);

  .close {
    font-size: 1.1em;
    padding: .6em;
    color: #bbb;
    // color: #666;;
  }

  // .buttons {
  //   .button {
  //     border-width: .9px;
  //   }
  //   .button:not(:last-of-type) {
  //     border-bottom: 0px;
  //     border-radius: 0px;
  //     margin-bottom: 0px;
  //   }

  //   .button:last-of-type {
  //     border-top-right-radius: 0px;
  //     border-top-left-radius: 0px;
  //   }
  //   .button:first-of-type {
  //     border-top-right-radius: 6px;
  //     border-top-left-radius: 6px;
  //   }
  // }
}
</style>
